import React from 'react';
import { Box, Button, Card, Chip, Collapse, Divider, Stack, Typography } from '@mui/material';
import {
    Cancel,
    CheckCircle,
    DoneAll,
    EventAvailable,
    ExpandLess,
    ExpandMore,
    LocalMall,
    Moped,
    NotificationsActive,
    OpenInNew,
    Payments,
    PlayArrow,
    PointOfSale,
    Restaurant,
    Schedule,
    ShoppingBag,
    Storefront,
    TableRestaurant,
    Undo
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Order, OrderStatus } from '@api/Orders/Order';
import { PaymentMethod, getOrderPaidBy } from '@api/Orders/OrderPayments';
import { getOrderLineDisplay } from '@utils/Order';
import { toDateSafe } from '@utils/Date';
import {
    BoardSeverity,
    OrderChannelKey,
    formatBoardClock,
    getBoardClock,
    getOrderChannel,
    getOrderShortNumber,
    getTakeAwayMode
} from '@utils/OrdersBoard';
import { currencyFormatter } from '@utils/Numbers';

export type BoardActionKind =
    | 'markPaid'
    | 'chargeInPos'
    | 'start'
    | 'ready'
    | 'complete'
    | 'closeTable'
    | 'openInList'
    | 'stepBack'
    | 'cancel';

export const CHANNEL_ICONS: Record<OrderChannelKey, React.ReactElement> = {
    mostrador: <Storefront sx={{ fontSize: 16 }} />,
    kiosko: <PointOfSale sx={{ fontSize: 16 }} />,
    mesa: <TableRestaurant sx={{ fontSize: 16 }} />,
    pickup: <ShoppingBag sx={{ fontSize: 16 }} />,
    delivery: <Moped sx={{ fontSize: 16 }} />
};

export const CHANNEL_LABELS: Record<OrderChannelKey, string> = {
    mostrador: 'Mostrador',
    kiosko: 'Kiosko',
    mesa: 'Mesa',
    pickup: 'Pickup',
    delivery: 'Delivery'
};

const SEVERITY_COLOR: Record<BoardSeverity, string> = {
    ok: 'success.main',
    warn: 'warning.main',
    late: 'error.main'
};

/** Con qué se pagó, leído de los montos de la orden (mixto = varios) */
const getPaymentLabel = (order: Order): string => {
    const methods: string[] = [];
    if (getOrderPaidBy(order, PaymentMethod.Cash) > 0) methods.push('Efectivo');
    if (getOrderPaidBy(order, PaymentMethod.CreditCard) > 0) methods.push('Tarjeta');
    if (getOrderPaidBy(order, PaymentMethod.BankTransfer) > 0) methods.push('Transferencia');
    if (methods.length === 0 && order.pointsAmount > 0) methods.push('Puntos');
    if (methods.length === 0 && order.stripe) methods.push('En línea');
    return methods.join(' + ');
};

interface PrimaryAction {
    kind: BoardActionKind;
    label: string;
    icon: React.ReactElement;
    color: 'primary' | 'success' | 'warning';
}

/**
 * El único botón que necesita el cocinero. Cada estado tiene UNA salida obvia;
 * lo que se sale de la cocina (Uber, links de ruta) manda a la vista de lista,
 * que es donde vive ese flujo.
 */
export const getPrimaryAction = (order: Order, canMarkPaid: boolean): PrimaryAction => {
    const channel = getOrderChannel(order).key;

    if (order.status === OrderStatus.WaitingForPayment) {
        if (order.deliveryType && canMarkPaid) {
            return { kind: 'markPaid', label: 'Marcar pagado', icon: <Payments />, color: 'success' };
        }
        return { kind: 'chargeInPos', label: 'Cobrar en caja', icon: <PointOfSale />, color: 'warning' };
    }

    if (order.status === OrderStatus.Paid) {
        return { kind: 'start', label: 'Comenzar', icon: <PlayArrow />, color: 'primary' };
    }

    if (order.status === OrderStatus.Preparing) {
        return { kind: 'ready', label: 'Marcar listo', icon: <CheckCircle />, color: 'success' };
    }

    if (order.status === OrderStatus.Ready) {
        if (channel === 'mesa') {
            return { kind: 'closeTable', label: 'Cerrar mesa y pagar', icon: <Payments />, color: 'success' };
        }
        if (channel === 'delivery') {
            return { kind: 'openInList', label: 'Enviar a ruta', icon: <Moped />, color: 'primary' };
        }
        return { kind: 'complete', label: 'Entregar', icon: <DoneAll />, color: 'success' };
    }

    return { kind: 'complete', label: 'Completar', icon: <DoneAll />, color: 'success' };
};

interface OrderBoardCardProps {
    order: Order;
    now: Date;
    expanded: boolean;
    canMarkPaid: boolean;
    /**
     * MODO COMPACTO (2026-08-19): en la tablet de cocina la tarjeta normal mide
     * ~250px y solo caben 1.5 en pantalla. Aquí se recorta lo que el cocinero NO
     * necesita para decidir: el chip de canal (el canal ya va como texto bajo el
     * número) y el de pago cuando ya está pagado (solo estorba; "en espera de
     * pago" y "cuenta abierta" SÍ se quedan, esos cambian lo que hace). El número,
     * el cronómetro, para llevar/comer aquí, la alarma de retraso, el cliente y el
     * botón de acción NO se tocan: son la tarjeta.
     */
    compact?: boolean;
    onToggleExpand: (orderId: string) => void;
    onAction: (order: Order, kind: BoardActionKind) => void;
}

const OrderBoardCard: React.FunctionComponent<OrderBoardCardProps> = (props) => {
    const { order, now, expanded, canMarkPaid, compact, onToggleExpand, onAction } = props;

    const channel = getOrderChannel(order);
    const clock = getBoardClock(order, now);
    const takeAway = getTakeAwayMode(order);
    const action = getPrimaryAction(order, canMarkPaid);
    const severityColor = SEVERITY_COLOR[clock.severity];
    const lines = (order.products || []).filter(Boolean);
    const totalItems = lines.reduce((sum, line) => sum + (getOrderLineDisplay(line).quantity || 0), 0);
    const isUnpaid = order.status === OrderStatus.WaitingForPayment;
    // Las mesas se cobran al cerrar la cuenta: no están "en espera de pago"
    const isOpenAccount = channel.key === 'mesa' && order.status !== OrderStatus.Paid;
    const paymentLabel = getPaymentLabel(order);
    const canStepBack = order.status === OrderStatus.Preparing || order.status === OrderStatus.Ready;
    // En compacto la fila de chips se queda sin el de canal, y un delivery pagado y
    // a tiempo no aporta ninguno: sin este guard quedaría una fila VACÍA que sigue
    // cobrando el spacing del Stack padre — 12px tirados en el modo que existe para
    // ahorrar pixeles.
    const showChipsRow = !compact || !!takeAway || !!clock.scheduledAt || clock.isLate;
    const orderDate = toDateSafe(order.date);

    return (
        <Card
            sx={{
                p: 0,
                // La columna es un flex vertical con scroll: sin esto las tarjetas se
                // encogen para caber (se quedan sin botones ni cliente) en vez de
                // conservar su alto y dejar que la columna scrollee.
                flexShrink: 0,
                // El borde izquierdo es el semáforo de tiempo; el resto del borde
                // marca la alarma y el pedido sin pagar. Las propiedades largas van
                // DESPUÉS del shorthand o `border` se las come.
                border: 1,
                borderStyle: isUnpaid ? 'dashed' : 'solid',
                borderColor: clock.isLate ? 'error.main' : 'divider',
                borderLeftWidth: 6,
                borderLeftStyle: 'solid',
                borderLeftColor: severityColor,
                bgcolor: clock.isLate ? '#FDF3F2' : 'background.paper',
                animation: clock.isLate ? 'boardPulse 1.8s ease-in-out infinite' : 'none',
                '@keyframes boardPulse': {
                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(211,47,47,0)' },
                    '50%': { boxShadow: '0 0 0 4px rgba(211,47,47,0.18)' }
                }
            }}
        >
            <Stack p={compact ? 1.25 : 2} spacing={compact ? 0.75 : 1.25}>
                {/* Número, origen y cronómetro */}
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Stack minWidth={0}>
                        <Typography variant={compact ? 'h5' : 'h4'} fontWeight={700} lineHeight={1}>
                            {`#${getOrderShortNumber(order)}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {channel.label}
                            {orderDate ? ` · ${format(orderDate, 'HH:mm')}` : ''}
                        </Typography>
                    </Stack>
                    <Box flex={1} />
                    <Stack alignItems="flex-end">
                        <Typography
                            variant={compact ? 'h6' : 'h5'}
                            fontWeight={700}
                            lineHeight={1}
                            color={severityColor}
                        >
                            {clock.mode === 'programado'
                                ? `en ${Math.ceil(clock.minutes)} min`
                                : formatBoardClock(clock.minutes)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {clock.mode === 'listo'
                                ? 'esperando'
                                : clock.mode === 'programado'
                                ? 'para entrar'
                                : `meta ${clock.slaMinutes} min`}
                        </Typography>
                    </Stack>
                </Stack>

                {/* Identificadores: canal, para llevar/comer aquí, programado, alarma */}
                {showChipsRow && (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {!compact && (
                            <Chip
                                size="small"
                                variant="outlined"
                                icon={CHANNEL_ICONS[channel.key]}
                                label={CHANNEL_LABELS[channel.key]}
                            />
                        )}
                        {takeAway === 'llevar' && (
                            <Chip
                                size="small"
                                variant="outlined"
                                icon={<LocalMall sx={{ fontSize: 16 }} />}
                                label="Para llevar"
                            />
                        )}
                        {takeAway === 'aqui' && (
                            <Chip
                                size="small"
                                variant="outlined"
                                icon={<Restaurant sx={{ fontSize: 16 }} />}
                                label="Comer aquí"
                            />
                        )}
                        {takeAway === 'mixto' && (
                            <Chip size="small" variant="outlined" color="info" label="Mostrador + llevar" />
                        )}
                        {clock.scheduledAt && (
                            <Chip
                                size="small"
                                color="success"
                                variant="outlined"
                                icon={
                                    order.isReservation ? (
                                        <EventAvailable sx={{ fontSize: 16 }} />
                                    ) : (
                                        <Schedule sx={{ fontSize: 16 }} />
                                    )
                                }
                                label={`${order.isReservation ? 'Reserva' : 'Programado'} ${format(
                                    clock.scheduledAt,
                                    'HH:mm'
                                )}`}
                            />
                        )}
                        {clock.isLate && (
                            <Chip
                                size="small"
                                color="error"
                                icon={<NotificationsActive sx={{ fontSize: 16, color: 'inherit' }} />}
                                label={`Retrasado ${Math.floor(clock.minutes)} min`}
                            />
                        )}
                    </Stack>
                )}

                {!compact && <Divider />}

                {/* Cliente y tamaño del pedido */}
                <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography fontWeight={600} noWrap flex={1}>
                        {order.customer?.profile?.name || 'Sin cliente'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
                        {`${totalItems} art. · ${currencyFormatter((order.total || 0))}`}
                    </Typography>
                </Stack>

                {/* Estado del pago. En compacto, "Pagado" se calla: no cambia nada de lo
                    que cocina tiene que hacer. Los otros dos sí. */}
                {(!compact || isUnpaid || isOpenAccount) && (
                    <Box>
                        {isUnpaid ? (
                            <Chip
                                size="small"
                                color="warning"
                                icon={<Payments sx={{ fontSize: 16 }} />}
                                label="En espera de pago"
                            />
                        ) : isOpenAccount ? (
                            <Chip
                                size="small"
                                color="info"
                                variant="outlined"
                                icon={<TableRestaurant sx={{ fontSize: 16 }} />}
                                label="Cuenta abierta · se cobra al cerrar"
                            />
                        ) : (
                            <Chip
                                size="small"
                                color="success"
                                variant="outlined"
                                icon={<Payments sx={{ fontSize: 16 }} />}
                                label={paymentLabel ? `Pagado · ${paymentLabel}` : 'Pagado'}
                            />
                        )}
                    </Box>
                )}

                {/* Acción principal: un clic, sin arrastrar */}
                <Stack direction="row" spacing={1}>
                    <Button
                        fullWidth
                        variant="contained"
                        color={action.color}
                        size={compact ? 'medium' : 'large'}
                        startIcon={action.icon}
                        onClick={() => onAction(order, action.kind)}
                    >
                        {action.label}
                    </Button>
                    {/* En compacto el detalle queda como icono, pero conserva su
                        nombre accesible: es el mismo botón, no otro. */}
                    <Button
                        variant="outlined"
                        aria-label={expanded ? 'Ocultar' : 'Detalle'}
                        title={expanded ? 'Ocultar detalle' : 'Ver detalle'}
                        onClick={() => onToggleExpand(order.id as string)}
                        sx={{ flexShrink: 0, minWidth: compact ? 44 : undefined, px: compact ? 0 : undefined }}
                    >
                        {compact ? expanded ? <ExpandLess /> : <ExpandMore /> : expanded ? 'Ocultar' : 'Detalle'}
                    </Button>
                </Stack>

                {/* Desglose del pedido dentro de la misma tarjeta */}
                <Collapse in={expanded} unmountOnExit>
                    <Stack spacing={1.5} pt={1}>
                        <Divider />
                        <Stack spacing={1}>
                            {lines.map((line, index) => {
                                const display = getOrderLineDisplay(line);
                                return (
                                    <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                                        <Typography fontWeight={700} minWidth={28}>
                                            {`${display.quantity}×`}
                                        </Typography>
                                        <Stack flex={1} minWidth={0}>
                                            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                                                <Typography fontWeight={600}>{display.name}</Typography>
                                                {takeAway === 'mixto' && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {display.takeAway ? '(para llevar)' : '(mostrador)'}
                                                    </Typography>
                                                )}
                                            </Stack>
                                            {display.modifiers.map((modifier, idx) => (
                                                <Typography key={idx} variant="body2" color="text.secondary">
                                                    {`+ ${modifier.quantity}× ${modifier.label}`}
                                                    {modifier.optionName ? ` (${modifier.optionName})` : ''}
                                                </Typography>
                                            ))}
                                            {display.removedIngredients.length > 0 && (
                                                <Typography variant="body2" color="error.main" fontWeight={600}>
                                                    {`SIN: ${display.removedIngredients.join(', ')}`}
                                                </Typography>
                                            )}
                                            {display.note && (
                                                <Typography variant="body2" fontStyle="italic">
                                                    {display.note}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Stack>
                                );
                            })}
                        </Stack>

                        {order.note && (
                            <Stack bgcolor="#FFF8E1" border="1px solid #E6C77A" borderRadius={1} p={1}>
                                <Typography variant="body2" fontWeight={600}>
                                    {`Nota: ${order.note}`}
                                </Typography>
                            </Stack>
                        )}

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<OpenInNew />}
                                onClick={() => onAction(order, 'openInList')}
                            >
                                Ver en lista
                            </Button>
                            {canStepBack && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Undo />}
                                    onClick={() => onAction(order, 'stepBack')}
                                >
                                    Regresar
                                </Button>
                            )}
                            <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<Cancel />}
                                onClick={() => onAction(order, 'cancel')}
                            >
                                Cancelar
                            </Button>
                        </Stack>
                    </Stack>
                </Collapse>
            </Stack>
        </Card>
    );
};

export default OrderBoardCard;
